import { Button, Col, Layout, Row, Skeleton, Space, Spin } from 'antd';
import { Conditional } from 'components/conditional/conditional';
import { useVoorwaardenModal } from 'components/voorwaarden-modal/useVoorwaardenModal';
import { klwAxios } from 'helpers/api';
import { atoms } from 'helpers/atoms';
import { useCallContext } from 'helpers/hooks/use-context';
import { useLogout } from 'helpers/hooks/use-logout';
import { useAtomValue } from 'jotai';
import { UserRole } from 'pages/home/components/modals/useChoiceModal';
import { useGetMaintenanceConfig } from 'pages/maintenance/models/queries';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { refreshCallContext } from 'helpers/queries/call-context';
import { AdvisorYearSelector } from './menu/advisor-year-selector';
import { LoginButton } from './menu/login-button';
import { MainMenu } from './menu/main-menu/main-menu';
import { ProfileAdminMenuItem } from './menu/profile-admin-menu-item';
import { YearSelector } from './menu/year-selector';
import { useGetMenu } from './models/queries';
import styles from './styles.module.scss';
import { useQueryClient } from '@tanstack/react-query';
const { Header } = Layout;
interface TopBarProps {}

export const MainHeader: React.FunctionComponent<TopBarProps> = () => {
  const keycloak = useAtomValue(atoms.auth.keycloakInstance);
  const nav = useNavigate();
  const context = useCallContext();
  const isLoggedIn = keycloak.authenticated && !!keycloak.token;
  const menu = useGetMenu(isLoggedIn);
  const maintenanceConfig = useGetMaintenanceConfig();
  const queryClient = useQueryClient();
  const logout = useLogout();
  const { voorwaardenModal } = useVoorwaardenModal(isLoggedIn);

  useEffect(() => {
    if (maintenanceConfig.data && maintenanceConfig.data?.maintenanceMode) nav('/maintenance');
  }, [maintenanceConfig.data?.maintenanceMode, nav]);

  useEffect(() => {
    if (maintenanceConfig.data?.maintenanceMode) return;
    if (!keycloak.authenticated) nav('/');
  }, [keycloak.authenticated, maintenanceConfig.data?.maintenanceMode, nav]);

  const isNieuweGebruiker = () => {
    if (!keycloak.authenticated) return false;
    if (!context.data) return false;
    if (context?.data?.gebruikerId === 0) return true;
    return false;
  };

  const isNietActiveRelatie = () => {
    if (!keycloak.authenticated) return false;
    return [2, 3, 4].includes(context?.data?.relatieStatusId ?? 0);
  };

  useEffect(() => {
    if (context.status !== 'success') return; // wait for context before redirecting

    if (isNieuweGebruiker()) nav('/Profiel/Index');
    else if (isNietActiveRelatie()) nav('/Relatie/Index');
  }, [context.data, context.status]);

  const isVeehouder = () => {
    if (!keycloak.authenticated) return false;
    const rol = klwAxios.defaults.headers.common['gekozen-rol'];
    if (!rol && !!context?.data?.gebruikerIsDeelnemer) return true;
    if (rol === UserRole.VEEHOUDER) return true;
    return false;
  };

  const isAdviseur = () => {
    if (!keycloak.authenticated) return false;
    const rol = klwAxios.defaults.headers.common['gekozen-rol'];
    if (!rol && context?.data?.gebruikerIsDeelnemer === false) return true;
    if (rol === UserRole.ADVISEUR) return true;
    return false;
  };

  return (
    <Header style={{ width: '100%', backgroundColor: '#F0F0F0', position: 'fixed', zIndex: 11, height: 85 }}>
      <div className={styles.containerKLW}>
        <Row gutter={[4, 4]} align="top">
          <Col>
            <img
              style={{ cursor: 'pointer' }}
              onClick={() => {
                void refreshCallContext(queryClient);
                queryClient.invalidateQueries({ queryKey: ['Menu'] });
                queryClient.invalidateQueries({ queryKey: ['Home'] });
                nav('/');
              }}
              src="/klwLogo.png"
            />
          </Col>
          <Col>
            <Conditional condition={!isNietActiveRelatie() && !!menu?.data?.menuItems && menu?.data?.menuItems.length > 0}>
              <Spin spinning={menu?.isFetching}>
                <MainMenu menu={menu} />
              </Spin>
            </Conditional>
            <Conditional condition={!isNietActiveRelatie() && menu?.isLoading}>
              <Space>
                <Skeleton.Button active style={{ height: 90 }} />
                <Skeleton.Button active style={{ height: 90 }} />
                <Skeleton.Button active style={{ height: 90 }} />
                <Skeleton.Button active style={{ height: 90 }} />
                <Skeleton.Button active style={{ height: 90 }} />
                <Skeleton.Button active style={{ height: 90 }} />
              </Space>
            </Conditional>
          </Col>
          <Col flex={1}>
            <div />
          </Col>
          <Conditional condition={!isNietActiveRelatie() && !isNieuweGebruiker() && isAdviseur()}>
            <Col span={2} />
            <Col>
              <AdvisorYearSelector menu={menu} />
            </Col>
            <Col>
              <ProfileAdminMenuItem menu={menu} />
            </Col>
          </Conditional>
          <Conditional condition={!isNietActiveRelatie() && isVeehouder()}>
            <Col span={2} />
            <Col>
              <YearSelector menu={menu} />
            </Col>
            <Col>
              <ProfileAdminMenuItem menu={menu} />
            </Col>
          </Conditional>
          <Conditional condition={isNietActiveRelatie() || isNieuweGebruiker()}>
            <Col span={2} />
            <Col>
              <ProfileAdminMenuItem menu={menu} />
            </Col>
          </Conditional>
          <Conditional condition={!keycloak.authenticated}>
            <Col span={3} />
            <Col />
            <Col>
              <LoginButton />
            </Col>
          </Conditional>
          <Conditional condition={!!keycloak.authenticated && !isAdviseur() && !isVeehouder() && !isNieuweGebruiker() && !menu?.isLoading}>
            <Col>
              <Button onClick={() => void logout()} type="primary" style={{ marginTop: 28 }}>
                Uitloggen
              </Button>
            </Col>
          </Conditional>
        </Row>
        {voorwaardenModal}
      </div>
    </Header>
  );
};
